import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Button, Stack, Paper, Chip, Avatar,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  Select, MenuItem, FormControl, InputLabel, Alert, Snackbar,
  IconButton, Tooltip, CircularProgress, InputAdornment, Divider,
  Autocomplete, Switch, FormControlLabel
} from '@mui/material';
import {
  PersonAdd, ArrowBack, Search, Refresh, LockReset, Mail,
  CheckCircle, Cancel, Edit, PeopleAlt, Person, LocalHospital,
  FamilyRestroom, AdminPanelSettings, BarChart, Download, Upload, Link as LinkIcon
} from '@mui/icons-material';
import { apiRequest } from '../../utils/api';
import { useAuthStore } from '../../stores/authStore';

const roleLabels = {
  patient: 'Paciente',
  employee: 'Acompañante',
  family: 'Familiar',
  admin: 'Admin',
};

const roleColors = {
  patient: '#2563EB',
  employee: '#7C3AED',
  family: '#10B981',
  admin: '#EF4444',
};

const roleIcons = {
  patient: <LocalHospital sx={{ fontSize: 16 }} />,
  employee: <Person sx={{ fontSize: 16 }} />,
  family: <FamilyRestroom sx={{ fontSize: 16 }} />,
  admin: <AdminPanelSettings sx={{ fontSize: 16 }} />,
};

export default function UserManagement() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuthStore();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Invite form
  const [inviteForm, setInviteForm] = useState({
    email: '', first_name: '', last_name: '', role: 'employee'
  });
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState('');

  // Patient search for family linking
  const [patientSearch, setPatientSearch] = useState('');
  const [patientOptions, setPatientOptions] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientSearchLoading, setPatientSearchLoading] = useState(false);

  // CSV import
  const fileInputRef = useRef(null);
  const [importResult, setImportResult] = useState(null);

  // User activity detail (F5)
  const [selectedUser, setSelectedUser] = useState(null);
  const [userActivity, setUserActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const data = await apiRequest('/users/');
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const data = await apiRequest('/admin/stats');
      setStats(data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchUsers(), fetchStats()]);
      setLoading(false);
    };
    load();
  }, [fetchUsers, fetchStats]);

  const handleInvite = async () => {
    setInviteError('');
    if (!inviteForm.email || !inviteForm.first_name || !inviteForm.last_name) {
      setInviteError('Completá todos los campos');
      return;
    }

    setInviting(true);
    try {
      const payload = { ...inviteForm };
      if (inviteForm.role === 'family' && selectedPatient) {
        payload.patient_id = selectedPatient.id;
      }
      await apiRequest('/admin/invite', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      setSnackbar({ open: true, message: 'Invitación enviada exitosamente', severity: 'success' });
      setInviteDialogOpen(false);
      setInviteForm({ email: '', first_name: '', last_name: '', role: 'employee' });
      setSelectedPatient(null);
      setPatientSearch('');
      fetchUsers();
      fetchStats();
    } catch (err) {
      setInviteError(err.response?.data?.error || err.message || 'Error al enviar invitación');
    } finally {
      setInviting(false);
    }
  };

  // Patient search for family linking
  useEffect(() => {
    if (patientSearch.length < 2) { setPatientOptions([]); return; }
    const timer = setTimeout(async () => {
      setPatientSearchLoading(true);
      try {
        const data = await apiRequest(`/admin/patients/search?q=${encodeURIComponent(patientSearch)}`);
        setPatientOptions(data.patients || []);
      } catch { setPatientOptions([]); }
      setPatientSearchLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [patientSearch]);

  // Role inline change
  const handleRoleChange = async (userId, newRole) => {
    if (!window.confirm(`¿Cambiar rol a ${roleLabels[newRole]}?`)) return;
    try {
      await apiRequest(`/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify({ role: newRole }),
      });
      setSnackbar({ open: true, message: 'Rol actualizado', severity: 'success' });
      fetchUsers();
      fetchStats();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Error', severity: 'error' });
    }
  };

  // CSV export
  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const resp = await fetch(`${apiUrl}/admin/export/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'usuarios.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch { setSnackbar({ open: true, message: 'Error al exportar', severity: 'error' }); }
  };

  // CSV import
  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const resp = await fetch(`${apiUrl}/admin/import-users`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await resp.json();
      setImportResult(data);
      setSnackbar({ open: true, message: `${data.created} usuarios creados, ${data.skipped} omitidos`, severity: data.created > 0 ? 'success' : 'warning' });
      fetchUsers(); fetchStats();
    } catch { setSnackbar({ open: true, message: 'Error al importar', severity: 'error' }); }
    e.target.value = '';
  };

  // User activity detail (F5)
  const handleUserClick = async (user) => {
    setSelectedUser(user);
    setActivityLoading(true);
    try {
      const data = await apiRequest(`/admin/users/${user.id}/activity`);
      setUserActivity(data);
    } catch { setUserActivity(null); }
    setActivityLoading(false);
  };

  const handleResendInvite = async (userId) => {
    try {
      await apiRequest(`/admin/resend-invitation/${userId}`, { method: 'POST' });
      setSnackbar({ open: true, message: 'Invitación reenviada', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Error al reenviar', severity: 'error' });
    }
  };

  const handleResetPassword = async (userId, userName) => {
    if (!window.confirm(`¿Enviar email de reinicio de contraseña a ${userName}?`)) return;
    try {
      await apiRequest(`/admin/reset-password/${userId}`, { method: 'POST' });
      setSnackbar({ open: true, message: 'Email de reinicio enviado', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Error al enviar reinicio', severity: 'error' });
    }
  };

  const handleToggleActive = async (userId, isActive) => {
    const action = isActive ? 'desactivar' : 'activar';
    if (!window.confirm(`¿Seguro que querés ${action} este usuario?`)) return;
    try {
      await apiRequest(`/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: !isActive }),
      });
      setSnackbar({ open: true, message: `Usuario ${isActive ? 'desactivado' : 'activado'}`, severity: 'success' });
      fetchUsers();
      fetchStats();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Error', severity: 'error' });
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = search === '' ||
      `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase().includes(search.toLowerCase());
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>
      {/* Header */}
      <Box sx={{ bgcolor: 'white', borderBottom: '1px solid', borderColor: 'divider', position: 'sticky', top: 0, zIndex: 30 }}>
        <Container maxWidth="xl">
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <IconButton onClick={() => navigate('/dashboard/employee')} size="small">
                <ArrowBack />
              </IconButton>
              <Typography variant="h5" sx={{
                fontWeight: 800, cursor: 'pointer',
                background: 'linear-gradient(135deg, #2563EB, #7C3AED)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}>
                Vincula
              </Typography>
              <Chip label="Gestión de Usuarios" size="small" variant="outlined" />
            </Stack>
            <Stack direction="row" spacing={1}>
              <input type="file" accept=".csv" ref={fileInputRef} onChange={handleImportCSV} style={{ display: 'none' }} />
              <Tooltip title="Importar CSV">
                <IconButton onClick={() => fileInputRef.current?.click()} sx={{ color: '#475569' }}>
                  <Upload />
                </IconButton>
              </Tooltip>
              <Tooltip title="Exportar CSV">
                <IconButton onClick={handleExportCSV} sx={{ color: '#475569' }}>
                  <Download />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<PersonAdd />}
                onClick={() => setInviteDialogOpen(true)}
                sx={{
                  background: 'linear-gradient(135deg, #2563EB, #7C3AED)',
                  borderRadius: 3, textTransform: 'none', fontWeight: 600,
                }}
              >
                Invitar usuario
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Stats */}
        {stats && (
          <Stack direction="row" spacing={2} sx={{ mb: 3, overflowX: 'auto' }}>
            {[
              { label: 'Total', value: stats.total_users, icon: <PeopleAlt />, color: '#475569' },
              { label: 'Activos', value: stats.active_users, icon: <CheckCircle />, color: '#10B981' },
              { label: 'Pendientes', value: stats.pending_invites, icon: <Mail />, color: '#F59E0B' },
              { label: 'Pacientes', value: stats.patients, icon: <LocalHospital />, color: '#2563EB' },
              { label: 'Acompañantes', value: stats.employees, icon: <Person />, color: '#7C3AED' },
              { label: 'Familiares', value: stats.families, icon: <FamilyRestroom />, color: '#10B981' },
            ].map((stat) => (
              <Paper key={stat.label} sx={{ p: 2, minWidth: 140, borderRadius: 3, flex: '1 0 auto' }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <Box sx={{ color: stat.color }}>{stat.icon}</Box>
                  <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
                </Stack>
                <Typography variant="h4" fontWeight={800} sx={{ color: stat.color }}>
                  {stat.value}
                </Typography>
              </Paper>
            ))}
          </Stack>
        )}

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <TextField
              size="small"
              placeholder="Buscar por nombre o email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ flex: 1 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search sx={{ color: '#94A3B8' }} /></InputAdornment>
              }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Rol</InputLabel>
              <Select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} label="Rol">
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="patient">Pacientes</MenuItem>
                <MenuItem value="employee">Acompañantes</MenuItem>
                <MenuItem value="family">Familiares</MenuItem>
                <MenuItem value="admin">Admins</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title="Refrescar">
              <IconButton onClick={() => { fetchUsers(); fetchStats(); }}>
                <Refresh />
              </IconButton>
            </Tooltip>
          </Stack>
        </Paper>

        {/* User List */}
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          {filteredUsers.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <PeopleAlt sx={{ fontSize: 48, color: '#CBD5E1', mb: 1 }} />
              <Typography color="text.secondary">No se encontraron usuarios</Typography>
            </Box>
          ) : (
            filteredUsers.map((u, idx) => (
              <React.Fragment key={u.id}>
                {idx > 0 && <Divider />}
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems={{ sm: 'center' }}
                  justifyContent="space-between"
                  sx={{ p: 2, '&:hover': { bgcolor: '#F8FAFC' } }}
                >
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ flex: 1 }}>
                    <Avatar sx={{
                      width: 40, height: 40,
                      bgcolor: `${roleColors[u.role]}15`,
                      color: roleColors[u.role],
                      fontSize: '0.85rem', fontWeight: 700,
                    }}>
                      {(u.first_name?.[0] || '?').toUpperCase()}
                    </Avatar>
                    <Box>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body1" fontWeight={600}
                          onClick={() => handleUserClick(u)}
                          sx={{ cursor: 'pointer', '&:hover': { color: '#2563EB', textDecoration: 'underline' } }}>
                          {u.first_name} {u.last_name}
                        </Typography>
                        {u.id !== currentUser?.id ? (
                          <Select
                            size="small"
                            value={u.role}
                            onChange={(e) => handleRoleChange(u.id, e.target.value)}
                            sx={{
                              height: 24, fontSize: 11, fontWeight: 600,
                              bgcolor: `${roleColors[u.role]}10`, color: roleColors[u.role],
                              '& .MuiSelect-select': { py: 0.25, px: 1 },
                              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                            }}
                          >
                            <MenuItem value="patient" sx={{ fontSize: 12 }}>Paciente</MenuItem>
                            <MenuItem value="employee" sx={{ fontSize: 12 }}>Acompañante</MenuItem>
                            <MenuItem value="family" sx={{ fontSize: 12 }}>Familiar</MenuItem>
                            <MenuItem value="admin" sx={{ fontSize: 12 }}>Admin</MenuItem>
                          </Select>
                        ) : (
                          <Chip
                            icon={roleIcons[u.role]}
                            label={roleLabels[u.role]}
                            size="small"
                            sx={{
                              bgcolor: `${roleColors[u.role]}10`,
                              color: roleColors[u.role],
                              fontWeight: 600, fontSize: 11, height: 24,
                              '& .MuiChip-icon': { color: roleColors[u.role] }
                            }}
                          />
                        )}
                        {u.is_active ? (
                          <Chip icon={<CheckCircle sx={{ fontSize: 12 }} />} label="Activo" size="small"
                            sx={{ bgcolor: '#D1FAE5', color: '#059669', height: 22, fontSize: 11 }} />
                        ) : (
                          <Chip icon={<Cancel sx={{ fontSize: 12 }} />} label="Pendiente" size="small"
                            sx={{ bgcolor: '#FEF3C7', color: '#D97706', height: 22, fontSize: 11 }} />
                        )}
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {u.email}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={0.5} sx={{ mt: { xs: 1, sm: 0 } }}>
                    {!u.is_active && (
                      <Tooltip title="Reenviar invitación">
                        <IconButton size="small" onClick={() => handleResendInvite(u.id)}
                          sx={{ color: '#F59E0B' }}>
                          <Mail fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {u.is_active && (
                      <Tooltip title="Reiniciar contraseña">
                        <IconButton size="small" onClick={() => handleResetPassword(u.id, `${u.first_name} ${u.last_name}`)}
                          sx={{ color: '#2563EB' }}>
                          <LockReset fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {u.id !== currentUser?.id && (
                      <Tooltip title={u.is_active ? 'Desactivar' : 'Activar'}>
                        <IconButton size="small"
                          onClick={() => handleToggleActive(u.id, u.is_active)}
                          sx={{ color: u.is_active ? '#EF4444' : '#10B981' }}>
                          {u.is_active ? <Cancel fontSize="small" /> : <CheckCircle fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </Stack>
              </React.Fragment>
            ))
          )}
        </Paper>
      </Container>

      {/* Invite Dialog */}
      <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Invitar nuevo usuario</DialogTitle>
        <DialogContent>
          {inviteError && <Alert severity="error" sx={{ mb: 2 }}>{inviteError}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth label="Nombre" value={inviteForm.first_name}
                onChange={(e) => setInviteForm({ ...inviteForm, first_name: e.target.value })}
              />
              <TextField
                fullWidth label="Apellido" value={inviteForm.last_name}
                onChange={(e) => setInviteForm({ ...inviteForm, last_name: e.target.value })}
              />
            </Stack>
            <TextField
              fullWidth label="Email" type="email" value={inviteForm.email}
              onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Rol</InputLabel>
              <Select value={inviteForm.role} onChange={(e) => { setInviteForm({ ...inviteForm, role: e.target.value }); setSelectedPatient(null); }} label="Rol">
                <MenuItem value="patient">Paciente</MenuItem>
                <MenuItem value="employee">Acompañante</MenuItem>
                <MenuItem value="family">Familiar</MenuItem>
                <MenuItem value="admin">Administrador</MenuItem>
              </Select>
            </FormControl>
            {inviteForm.role === 'family' && (
              <Autocomplete
                options={patientOptions}
                getOptionLabel={(o) => `${o.first_name} ${o.last_name} (${o.email})${!o.is_active ? ' — pendiente' : ''}`}
                value={selectedPatient}
                onChange={(_, v) => setSelectedPatient(v)}
                inputValue={patientSearch}
                onInputChange={(_, v) => setPatientSearch(v)}
                loading={patientSearchLoading}
                noOptionsText={patientSearch.length < 2 ? 'Escribí al menos 2 caracteres' : 'Sin resultados'}
                renderInput={(params) => (
                  <TextField {...params} label="Vincular a paciente (opcional)" placeholder="Buscar por nombre o email..." />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                      <LocalHospital sx={{ fontSize: 16, color: '#2563EB' }} />
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{option.first_name} {option.last_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{option.email}</Typography>
                      </Box>
                      {!option.is_active && (
                        <Chip label="Pendiente" size="small" sx={{ ml: 'auto', bgcolor: '#FEF3C7', color: '#D97706', height: 20, fontSize: 10 }} />
                      )}
                    </Stack>
                  </li>
                )}
              />
            )}
            {inviteForm.role === 'family' && selectedPatient && !selectedPatient.is_active && (
              <Alert severity="info" sx={{ fontSize: 13 }}>
                El paciente aún no activó su cuenta. El familiar se creará igualmente y se vinculará.
              </Alert>
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Se creará la cuenta y se enviará un email con un enlace para que el usuario elija su contraseña.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={() => setInviteDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            variant="contained" onClick={handleInvite} disabled={inviting}
            startIcon={inviting ? <CircularProgress size={16} color="inherit" /> : <PersonAdd />}
            sx={{
              background: 'linear-gradient(135deg, #2563EB, #7C3AED)',
              textTransform: 'none', fontWeight: 600, borderRadius: 2,
            }}
          >
            {inviting ? 'Enviando...' : 'Enviar invitación'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* User Activity Dialog (F5) */}
      <Dialog open={!!selectedUser} onClose={() => { setSelectedUser(null); setUserActivity(null); }}
        maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {selectedUser?.first_name} {selectedUser?.last_name}
        </DialogTitle>
        <DialogContent>
          {activityLoading ? (
            <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress /></Box>
          ) : userActivity ? (
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {[
                { label: 'Total llamadas', value: userActivity.total_calls || 0 },
                { label: 'Última llamada', value: userActivity.last_call_at ? new Date(userActivity.last_call_at).toLocaleDateString('es-AR') : 'Nunca' },
                { label: 'Duración acumulada', value: userActivity.total_duration ? `${Math.round(userActivity.total_duration / 60)} min` : '—' },
                { label: 'Último login', value: userActivity.last_login ? new Date(userActivity.last_login).toLocaleString('es-AR') : 'Sin datos' },
                { label: 'Última IP', value: userActivity.last_ip || '—' },
                { label: 'Recordatorios activos', value: userActivity.active_reminders || 0 },
                { label: 'Alertas totales', value: userActivity.total_alerts || 0 },
                { label: 'Vínculos familiares', value: userActivity.family_links || 0 },
              ].map(item => (
                <Stack key={item.label} direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                  <Typography variant="body2" fontWeight={600}>{item.value}</Typography>
                </Stack>
              ))}
            </Stack>
          ) : (
            <Typography color="text.secondary" sx={{ py: 2 }}>No se pudo cargar la actividad</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setSelectedUser(null); setUserActivity(null); }} sx={{ textTransform: 'none' }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
